import { useState } from 'react'
import { ArrowRight, Compass, X } from 'lucide-react'

import { VERSION } from '@core/release'
import {
  RIBBON_MOVES,
  decidirERegistar,
  dispensarNota,
} from '@core/layout/ribbon/ribbonMoves'

/** "O que mudou de sítio": a nota que acompanha a reorganização do
 *  friso da beta.123.
 *
 *  Só aparece a quem usou o friso antigo neste browser (ver
 *  ribbonMoves.ts), e desaparece para sempre ao ser dispensada.
 *
 *  Não há botão para voltar ao friso antigo. Manter dois frisos em
 *  paralelo era duplicar a lista de comandos e garantir que um dos dois
 *  ficava para trás a cada comando novo. A nota resolve o problema real,
 *  que é não saber para onde foram as coisas. */
export default function RibbonMovesNotice() {
  // A decisão já foi tomada no arranque (main.tsx). Aqui só se lê.
  const [visivel, setVisivel] = useState(() => decidirERegistar(VERSION))
  const [aberta, setAberta] = useState(false)

  if (!visivel) return null

  const fechar = () => {
    dispensarNota()
    setVisivel(false)
  }

  return (
    <div className="border-b border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-4 py-2 text-sm text-[var(--color-warn-text)]">
      <div className="flex items-center gap-3">
        <Compass className="h-4 w-4 shrink-0 text-[var(--color-warn-text)]" aria-hidden />
        <p className="min-w-0 flex-1">
          O friso foi reorganizado: os comandos passaram a estar agrupados
          pelo que fazes, e alguns mudaram de separador.
        </p>
        <button
          type="button"
          onClick={() => setAberta((v) => !v)}
          aria-expanded={aberta}
          className="shrink-0 rounded border border-[var(--color-warn-border)] bg-[var(--color-accent-bg)] px-3 py-1 font-medium text-amber-50 hover:bg-[var(--color-accent-bg)]"
        >
          {aberta ? 'Esconder' : 'O que mudou de sítio'}
        </button>
        <button
          type="button"
          onClick={fechar}
          className="shrink-0 rounded border border-[var(--color-warn-border)] px-3 py-1 text-[var(--color-warn-text)] hover:bg-[var(--color-accent-bg)]"
        >
          Percebi
        </button>
        <button
          type="button"
          aria-label="Dispensar a nota sobre o friso"
          title="Dispensar"
          onClick={fechar}
          className="shrink-0 rounded p-1 text-[var(--color-warn-text)] hover:bg-[var(--color-accent-bg)] hover:text-[var(--color-warn-text)]"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {aberta && (
        <ul className="mt-2 space-y-1.5 border-t border-[var(--color-warn-border)] pt-2">
          {RIBBON_MOVES.map((m) => (
            <li key={m.comando} className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium text-amber-50">{m.comando}</span>
              <span className="inline-flex items-center gap-1 text-[var(--color-warn-text)]">
                <span className="line-through">{m.de}</span>
                <ArrowRight className="h-3 w-3" aria-hidden />
                <span className="font-medium text-[var(--color-warn-text)] no-underline">
                  {m.para}
                </span>
              </span>
              <span className="w-full text-xs text-[var(--color-warn-text)]/70">{m.porque}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
